import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo, ReactNode } from 'react'
import { ApiKeysApi, ApiKey } from '@/services/users/apiKeysApi'
import { PoliticalCampaignsApi } from '@/services/political/politicalCampaignsApi'
import { useCurrentUser } from './UserContext'
import { useModule } from './ModuleContext'

interface NotificationsState {
  notifications: ApiKey[]
  /** Every invite, including ones already linked to some campaign. */
  allNotifications: ApiKey[]
  count: number
  loading: boolean
  refresh: () => void
}

const NotificationsContext = createContext<NotificationsState>({
  notifications: [],
  allNotifications: [],
  count: 0,
  loading: false,
  refresh: () => {},
})

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const user = useCurrentUser()
  const { currentModule } = useModule()
  const isPolitical = currentModule?.id === 'political'
  const [allNotifications, setAllNotifications] = useState<ApiKey[]>([])
  const [linkedPublicIds, setLinkedPublicIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true)
      const [keys, campaigns] = await Promise.all([
        isPolitical ? ApiKeysApi.getCrossNetwork('UPDATE_ADVERTISER') : Promise.resolve([]),
        isPolitical ? PoliticalCampaignsApi.getAll().catch(() => []) : Promise.resolve([]),
      ])
      setAllNotifications(keys)
      const usedIds = new Set<string>()
      for (const c of campaigns) {
        for (const a of c.externalApprovals) usedIds.add(a.id)
      }
      setLinkedPublicIds(usedIds)
    } catch {
      setAllNotifications([])
      setLinkedPublicIds(new Set())
    } finally {
      setLoading(false)
    }
  }, [isPolitical])

  useEffect(() => {
    if (!user) {
      setAllNotifications([])
      setLinkedPublicIds(new Set())
      return
    }

    fetchNotifications()

    intervalRef.current = setInterval(fetchNotifications, 60_000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [user?.id, isPolitical, fetchNotifications])

  // Filter out notifications already linked to a campaign
  const notifications = useMemo(
    () => allNotifications.filter((n) => {
      const pid = n.advertiser?.publicId
      return !pid || !linkedPublicIds.has(pid)
    }),
    [allNotifications, linkedPublicIds],
  )

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        allNotifications,
        count: notifications.length,
        loading,
        refresh: fetchNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  return useContext(NotificationsContext)
}
